import { useEffect, useRef, useState } from 'react';
import logo from '../../assets/logoHeader.png';
import favoriteIcon from '../../assets/Favorite.png';
import cartIcon from '../../assets/cart.png';
import profileIcon from '../../assets/profile.png';
import searchIcon from '../../assets/search.png';
import { isLoggedIn, logout, redirectToLogin } from '../../services/auth';
import { getCartCount, subscribeToCart } from '../../services/cart';
import { Api } from '../../services/api';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useLanguage } from '../../context/LanguageContext';
import { searchAll } from '../../services/search';

const NAV_LINKS = [
  { labelKey: 'nav.home', href: '/' },
  { labelKey: 'nav.categories', href: '/categories' },
  { labelKey: 'nav.inquiry', href: '/inquiry' },
  { labelKey: 'nav.support', href: '/support' },
];

const HEADER_ICONS = [
  { id: 'cart', icon: cartIcon, alt: 'Cart' },
  { id: 'favorite', icon: favoriteIcon, alt: 'Favorites' },
  { id: 'profile', icon: profileIcon, alt: 'Profile' },
];

const resolveIsActive = (href, pathname) => {
  if (!href.startsWith('/')) return false;
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
};

const Header = () => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuToggleRef = useRef(null);
  const searchBoxRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({
    products: [],
    brands: [],
    categories: [],
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const { t } = useLanguage();
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '/';

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
      if (
        mobileNavOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(event.target)
      ) {
        setMobileNavOpen(false);
      }
    };

    document.addEventListener('click', handleClickAway);
    return () => document.removeEventListener('click', handleClickAway);
  }, [mobileNavOpen]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    setCartCount(getCartCount());
    const unsubscribe = subscribeToCart((nextCart) => {
      const count = Array.isArray(nextCart?.items)
        ? nextCart.items.reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0
          )
        : 0;
      setCartCount(count);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isLoggedIn()) {
        setFavoriteCount(0);
        return;
      }
      try {
        const meta = JSON.parse(window.localStorage.getItem('myUser') || '{}');
        if (!meta?.customer_id) {
          setFavoriteCount(0);
          return;
        }
        const response = await Api.fetchFavorites(meta.customer_id);
        const payload = response?.data?.data || response?.data || [];
        setFavoriteCount(Array.isArray(payload) ? payload.length : 0);
      } catch {
        setFavoriteCount(0);
      }
    };
    fetchFavorites();
  }, []);

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
  };

  const openProduct = (product) => {
    if (typeof window === 'undefined') return;
    window.location.assign(
      `/product/${encodeURIComponent(product.productId || product.id)}`
    );
  };

  const openBrand = (brand) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('brand', brand.id);
    params.set('brandName', brand.name);
    window.location.assign(`/products?${params.toString()}`);
  };

  const openCategory = (category) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('category', category.id);
    window.location.assign(`/products?${params.toString()}`);
  };

  const executeSearch = async (term) => {
    const trimmed = term.trim();
    if (!trimmed) {
      setSearchResults({ products: [], brands: [], categories: [] });
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const payload = await searchAll(trimmed);
      setSearchResults(payload);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    const trimmed = value.trim();
    setSearchOpen(Boolean(trimmed));
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!trimmed) {
      setSearchResults({ products: [], brands: [], categories: [] });
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      executeSearch(trimmed);
    }, 250);
  };

  const goToSearchPage = () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      params.set('q', trimmed);
      window.location.assign(`/search?${params.toString()}`);
    }
    setSearchOpen(false);
  };

  const previewResults = (() => {
    const combined = [
      ...searchResults.products.map((product) => ({
        id: `product-${product.productId}`,
        type: 'product',
        title: product.name,
        subtitle: product.brand,
        onSelect: () => openProduct(product),
      })),
      ...searchResults.categories.map((category) => ({
        id: `category-${category.id}`,
        type: 'category',
        title: category.name,
        subtitle: t('search.categories_label'),
        onSelect: () => openCategory(category),
      })),
      ...searchResults.brands.map((brand) => ({
        id: `brand-${brand.id}`,
        type: 'brand',
        title: brand.name,
        subtitle: t('search.brands_label'),
        onSelect: () => openBrand(brand),
      })),
    ];
    return combined.slice(0, 4);
  })();

  const handleToggleMobileNav = () => {
    setMobileNavOpen((prev) => !prev);
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <>
      <header className={`site-header ${mobileNavOpen ? 'is-mobile-open' : ''}`}>
        <div className="site-header__container">
          <button
            type="button"
            className={`site-header__menu-toggle ${mobileNavOpen ? 'is-open' : ''}`}
            aria-label="Toggle navigation"
            aria-expanded={mobileNavOpen}
            onClick={handleToggleMobileNav}
            ref={menuToggleRef}
          >
            <span />
            <span />
            <span />
          </button>
          <a href="/" className="site-header__brand">
            <img src={logo} alt="Wassini Luxury" className="site-header__logo" />
          </a>

          <div
            className={`site-header__nav-wrapper ${
              mobileNavOpen ? 'is-open' : ''
            }`}
            ref={mobileMenuRef}
          >
            <nav className="site-header__nav" id="primary-navigation">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`site-header__nav-link ${
                    resolveIsActive(link.href, pathname) ? 'active' : ''
                  }`}
                  onClick={closeMobileNav}
                >
                  {t(link.labelKey)}
                </a>
              ))}
            </nav>

            <div className="site-header__search" ref={searchBoxRef}>
          <input
            type="search"
            placeholder={t('header.search_placeholder')}
            value={searchQuery}
            onChange={(event) => handleSearchChange(event.target.value)}
            onFocus={() => {
              if (searchQuery.trim()) {
                setSearchOpen(true);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                goToSearchPage();
              }
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="search-clear"
              aria-label="Clear search"
              onClick={() => {
                setSearchQuery('');
                setSearchResults({ products: [], brands: [], categories: [] });
                setSearchOpen(false);
              }}
            >
              ×
            </button>
          )}
          <button
            type="button"
            className="search-icon"
            aria-label="Search"
            onClick={goToSearchPage}
          >
            <img src={searchIcon} alt="" aria-hidden="true" />
          </button>
          {searchOpen && (
            <div className="search-dropdown">
              {searchLoading ? (
                <p className="search-dropdown__status">{t('search.loading')}</p>
              ) : previewResults.length ? (
                <>
                  <ul>
                    {previewResults.map((entry) => (
                      <li key={entry.id}>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={entry.onSelect}
                        >
                          <span className="search-dropdown__title">
                            {entry.title}
                          </span>
                          {entry.subtitle ? (
                            <span className="search-dropdown__subtitle">
                              {entry.subtitle}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="search-dropdown__more"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={goToSearchPage}
                  >
                    {t('search.view_more')}
                  </button>
                </>
              ) : (
                <p className="search-dropdown__status">
                  {t('search.preview_no_results')}
                </p>
              )}
            </div>
          )}
        </div>
          </div>

          <div className="site-header__icons">
          {HEADER_ICONS.map((item) =>
            item.id === 'profile' ? (
              <div
                key={item.id}
                className="site-header__profile"
                ref={profileRef}
              >
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={item.alt}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isLoggedIn()) {
                      setProfileMenuOpen((prev) => !prev);
                    } else {
                      redirectToLogin();
                    }
                  }}
                >
                  <img src={item.icon} alt={item.alt} />
                </button>
                {profileMenuOpen && isLoggedIn() && (
                  <div
                    className="profile-dropdown"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <a href="/profile">{t('profile.view_profile')}</a>
                    <a href="/orders">{t('profile.order_history')}</a>
                    <a href="/addresses">{t('header.addresses.manage_link')}</a>
                    <button type="button" onClick={handleLogout}>
                      {t('profile.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                key={item.id}
                type="button"
                className="icon-btn"
                aria-label={item.alt}
                onClick={() => {
                  if (item.id === 'cart') {
                    if (typeof window !== 'undefined') {
                      window.location.assign('/cart');
                    }
                    return;
                  }
                  if (item.id === 'favorite') {
                    if (!isLoggedIn()) {
                      redirectToLogin();
                      return;
                    }
                    window.location.assign('/favorites');
                  }
                }}
              >
                <img
                  src={
                    item.id === 'favorite' && favoriteCount > 0
                      ? favoriteIcon
                      : item.icon
                  }
                  alt={item.alt}
                />
                {item.id === 'cart' && cartCount > 0 && (
                  <span className="cart-badge">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
            )
          )}
          <LanguageSwitcher />
        </div>
      </div>
        <button
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          className={`site-header__backdrop ${mobileNavOpen ? 'is-visible' : ''}`}
          onClick={closeMobileNav}
        />
      </header>
    </>
  );
};

export default Header;
